import { renderToString } from 'react-dom/server';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { apparelProductTypeIds } from 'src/api/productTypes';
import ApparelIcon from 'src/component/Icon/IconApparel';
import i18n from 'src/i18n';
import sendPostMessage from 'src/store/app/operation/sendPostMessage';
import { EditorTabNames, Feature, MESSAGE_TYPE } from 'src/store/app/types';
import { Store } from 'src/store/index';
import isFeatureEnabled from 'src/util/feature/isFeatureEnabled';
import useIntegrationLayer from 'src/util/hook/useIntegrationLayer';

type CustomTabInfo = {
  id: string;
  translationKey?: string;
  title: string;
} & ({ iconName: string } | { iconUrl: string } | { iconSVG: string });

type TabInfo = EditorTabNames | CustomTabInfo;

interface GroupedTabData {
  groupedItems: TabInfo[][];
  stickyItems: TabInfo[];
}

const isTabVisible = (tab: EditorTabNames): boolean => {
  switch (tab) {
    case EditorTabNames.ADDONS:
      return isFeatureEnabled(Feature.supportClipArt);
    case EditorTabNames.GALLERY:
      return true;
    case EditorTabNames.TEXTS:
      return isFeatureEnabled(Feature.allowTexts);
    case EditorTabNames.LAYERS:
      return true;
      // return isFeatureEnabled(Feature.allowLayers) ?? true;
    case EditorTabNames.LAYOUTS:
      return true;
      // return isFeatureEnabled(Feature.allowLayouts);
    case EditorTabNames.FORMATS:
      return isFeatureEnabled(Feature.allowFormats);
    case EditorTabNames.SETTINGS:
      return true;
      // return isFeatureEnabled(Feature.allowSettings);
    case EditorTabNames.WARNINGS:
      return true;
    case EditorTabNames.SHAPES:
      return false;
    default:
      return false;
  }
};

const sendSetEditorTabsPostMessage = () => (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
) => {
  const integrationLayer = useIntegrationLayer();

  // Define tab groups based on the example format
  const tabGroups: EditorTabNames[][] = [

    // Group 1: Main design tabs
    [
      EditorTabNames.FORMATS,
      EditorTabNames.GALLERY,
      EditorTabNames.TEXTS,
      EditorTabNames.LAYOUTS,
      EditorTabNames.ADDONS,
      EditorTabNames.LAYERS,
      EditorTabNames.WARNINGS,
    ],
    // Group 2: Settings
    [
      EditorTabNames.SETTINGS,
    ],
  ];

  // Filter each group to only include visible tabs
  let groupedItems: TabInfo[][] = tabGroups
    .map((group) => group.filter((tab) => isTabVisible(tab)))
    .filter((group) => group.length > 0); // Remove empty groups

  // Handle special case for FORMATS tab with custom icon for apparel products
  if (isFeatureEnabled(Feature.allowFormats) && apparelProductTypeIds.indexOf(integrationLayer.productTypeId) !== -1) {
    groupedItems = groupedItems.map((group) => group.map((tab): TabInfo => {
      if (tab === EditorTabNames.FORMATS) {
        return {
          id: EditorTabNames.FORMATS,
          title: i18n.t('editor-product'),
          iconName: 'formats',
          iconSVG: renderToString(ApparelIcon()),
        };
      }
      return tab;
    }));
  }

  const groupedTabs: GroupedTabData = {
    groupedItems,
    stickyItems: [],
  };

  sendPostMessage({
    type: MESSAGE_TYPE['editor.setGroupedSidebarTabs'],
    data: groupedTabs,
  });

  const { isMobile } = useIntegrationLayer();
  if (!isMobile) {
    sendPostMessage({
      type: MESSAGE_TYPE['editor.setSidebarActiveTabName'],
      data: EditorTabNames.GALLERY,
    });
  }
};

export default sendSetEditorTabsPostMessage;
